/**
 * «Дело» карантинного контура — что произошло и на основании чего (07.10.2026).
 *
 * Раньше раскрытие строки «Предписания» рисовало цепочку из сводки
 * `/quarantine/statuses`: там только последняя находка без даты, метода и
 * замеров, и шаги выходили пустыми («повторного замера ещё не было» при
 * состоявшемся замере). Теперь дело собирается из двух чтений карточки
 * контура — тех же, что у вкладки «Карантин» карточки:
 *
 * * `/contours/{id}` → `quarantine`: интервалы статуса с причиной («находка
 *   #2093», «повторное измерение #2016»), обследования (дата, инспектор),
 *   находки (вид, направление, масштаб, примечание);
 * * `/contours/{id}/history` → `requirements`: предписание к находке (метод,
 *   выдано, срок, устранено, состояние) и повторные замеры.
 *
 * Работа хозяйства по предписанию — обязательство по находке и его отчёт —
 * приходит в `requirements[].requirement.works` (журнал №659): отчёт значит
 * «обработку провели», устранение подтверждает только замер.
 *
 * Волна 1б (№646–650): у находки — класс вида (карантинный объект / прочий
 * вредный организм / не указан), часть контура и земля под сигналом;
 * решения ответственного карантинной службы и направления в агроинспекцию —
 * события дела; состояние случая («обработали, ждём контроля», «требует
 * решения»). Ввод инспектора и ответственного — :component:`QuarantineEntry`.
 *
 * Чего в базе нет, сказано словами в конце дела, а не спрятано: документы и
 * комментарии.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useRequest } from '../../../api/useRequest';
import { api, request, type ContourDetail, type QueryParams } from '../../../api/client';
import type { ContourHistory } from '../cabinet/model';
import { LoadError, failed } from '../../ui/LoadError';
import { Skeleton } from '../../ui/Skeleton';
import { dateShort, num } from '../../lib/format';
import { defineMessages, type Message } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { tr , formatNum } from '../../../i18n/runtime';
import { findingWorkText } from './findingWork';
import { WorkCycleLink } from './PrescriptionsBlock';
import { QuarantineEntry } from './QuarantineEntry';
import { CycleChoiceButtons, type CycleOption } from '../../ui/CycleChoiceButtons';
import { CASE_STATE_WORD, CLASS_LONG, OUTSIDE_CYCLE_WORD } from './quarantineArea';
import { STAGE_ICON } from '../../widgets/prescriptions/caseIcons';
import type { Stage } from '../../widgets/prescriptions/prescriptionsModel';
import type { LucideIcon } from 'lucide-react';
import { Icon } from '../../ui/Icon';

type Event = { date: string | null; stage?: Stage; kind: 'inspection' | 'finding' | 'requirement' | 'followup' | 'decision' | 'status' | 'now'; title: string; text?: ReactNode; tone?: 'red' | 'yellow' | 'green' | 'muted' };

const m = defineMessages({
  workAwaitsCycle: { id: 'quarantine.case.event.awaitsCycle', defaultMessage: 'Работа ждёт выбора агроцикла', description: 'Событие в деле контура: на поле два агроцикла, менеджер выбирает агроцикл работы по предписанию одним нажатием' },
  classUnspecified: { id: 'quarantine.case.classUnspecified', defaultMessage: 'класс вида не указан', description: 'Класс вида в строке находки дела контура: класс не указан; строчными' },
  decisionRed: { id: 'quarantine.case.decision.setRed', defaultMessage: 'Решение: красный', description: 'Заголовок события дела контура: ответственный поставил красный статус' },
  decisionLift: { id: 'quarantine.case.decision.liftRed', defaultMessage: 'Решение: красный снят', description: 'Заголовок события дела контура: ответственный снял красный статус' },
  decisionKeep: { id: 'quarantine.case.decision.keep', defaultMessage: 'Решение: без красного', description: 'Заголовок события дела контура: ответственный оставил без красного статуса' },
  ha: { id: 'quarantine.case.ha', defaultMessage: '{value} га', description: 'Гектары после числа; {value} — число, уже отформатированное' },
  dirPests: { id: 'quarantine.case.direction.pests', defaultMessage: 'вредители', description: 'Направление находки в строке дела контура: вредители; строчными' },
  dirDiseases: { id: 'quarantine.case.direction.diseases', defaultMessage: 'болезни', description: 'Направление находки в строке дела контура: болезни растений; строчными' },
  dirWeeds: { id: 'quarantine.case.direction.weeds', defaultMessage: 'сорняки', description: 'Направление находки в строке дела контура: сорняки; строчными' },
  scaleSpot: { id: 'quarantine.case.scale.spot', defaultMessage: 'очаг', description: 'Масштаб находки: небольшой очаг; строчными' },
  scalePartial: { id: 'quarantine.case.scale.partial', defaultMessage: 'масштаб: поражена часть', description: 'Масштаб находки: поражена часть контура; строчными' },
  scaleWhole: { id: 'quarantine.case.scale.whole', defaultMessage: 'масштаб: поражено всё', description: 'Масштаб находки: поражён весь контур; строчными' },
  outcomeEliminated: { id: 'quarantine.case.outcome.eliminated', defaultMessage: 'очаг устранён', description: 'Итог повторного замера в деле контура: очаг устранён (подтверждает только замер инспектора); строчными' },
  outcomeReduced: { id: 'quarantine.case.outcome.reduced', defaultMessage: 'очаг уменьшился', description: 'Итог повторного замера в деле контура: очаг уменьшился; строчными' },
  outcomeUnchanged: { id: 'quarantine.case.outcome.unchanged', defaultMessage: 'очаг сохранился', description: 'Итог повторного замера в деле контура: очаг не изменился; строчными' },
  outcomeIncreased: { id: 'quarantine.case.outcome.increased', defaultMessage: 'очаг увеличился', description: 'Итог повторного замера в деле контура: очаг вырос; строчными' },
  statusGreen: { id: 'quarantine.case.status.green', defaultMessage: 'зелёный', description: 'Статус контура в деле: зелёный; строчными, после «Статус:»' },
  statusYellow: { id: 'quarantine.case.status.yellow', defaultMessage: 'жёлтый', description: 'Статус контура в деле: жёлтый (наблюдение); строчными, после «Статус:»' },
  statusRed: { id: 'quarantine.case.status.red', defaultMessage: 'красный', description: 'Статус контура в деле: красный; строчными, после «Статус:»' },
  stateOpen: { id: 'quarantine.case.state.open', defaultMessage: 'срок не истёк', description: 'Сейчас по предписанию: срок не истёк; строчными, после «Сейчас:»' },
  stateOverdue: { id: 'quarantine.case.state.overdue', defaultMessage: 'просрочено', description: 'Сейчас по предписанию: срок прошёл, устранения нет; строчными, после «Сейчас:»' },
  stateExtended: { id: 'quarantine.case.state.extended', defaultMessage: 'срок продлён — дальше по новому предписанию', description: 'Сейчас по предписанию: срок продлён решением службы, случай продолжает новое предписание с новым сроком; это предписание не открыто; строчными, после «Сейчас:»' },
  stateEliminated: { id: 'quarantine.case.state.eliminated', defaultMessage: 'устранено в срок', description: 'Сейчас по предписанию: устранено по замеру в срок; строчными, после «Сейчас:»' },
  stateEliminatedLate: { id: 'quarantine.case.state.eliminatedLate', defaultMessage: 'устранено с опозданием', description: 'Сейчас по предписанию: устранено по замеру, но после срока; строчными, после «Сейчас:»' },
  loadWhat: { id: 'quarantine.case.loadWhat', defaultMessage: 'дело контура', description: 'Что не загрузилось, в винительном падеже, для сообщения «Не удалось загрузить …»' },
  beforeTitle: { id: 'quarantine.case.before.title', defaultMessage: 'Плановые обследования без находок: {count}', description: 'Заголовок свёрнутой строки дела контура: плановые обследования до первой находки; {count} — число обследований' },
  beforeRange: { id: 'quarantine.case.before.range', defaultMessage: '{from} — {to}', description: 'Диапазон дат под заголовком «Плановые обследования без находок»; {from}/{to} — даты' },
  inspection: { id: 'quarantine.case.inspection', defaultMessage: 'Обследование', description: 'Заголовок события дела контура: обследование' },
  inspector: { id: 'quarantine.case.inspector', defaultMessage: 'инспектор: {name}{hasPart, select, yes { · часть {part}} other {}}', description: 'Текст события «Обследование»; {name} — инспектор (или «не указан»), {part} — часть контура, если обследована не вся' },
  inspectorUnknown: { id: 'quarantine.case.inspectorUnknown', defaultMessage: 'не указан', description: 'Вместо имени инспектора, когда не указан; строчными' },
  findingTitle: { id: 'quarantine.case.finding.title', defaultMessage: 'Находка: {name}', description: 'Заголовок события дела контура: находка; {name} — вид вредного организма' },
  speciesUnnamed: { id: 'quarantine.case.finding.speciesUnnamed', defaultMessage: 'вид не назван', description: 'Вместо названия вида в находке, когда вид не назван; строчными' },
  findingPart: { id: 'quarantine.case.finding.part', defaultMessage: 'часть {part}', description: 'Часть контура, на которой находка; {part} — название части' },
  findingWhole: { id: 'quarantine.case.finding.whole', defaultMessage: 'обследован контур целиком', description: 'Находка на всём контуре; строчными' },
  findingSignal: { id: 'quarantine.case.finding.signal', defaultMessage: 'под сигналом {area}{unspecified, select, yes { (часть не указана — вся единица)} other {}}', description: 'Площадь под сигналом в строке находки; {area} — «12 га», {unspecified} — yes, если поражённая часть не указана и взята вся единица' },
  officerRole: { id: 'quarantine.case.officerRole', defaultMessage: 'ответственный карантинной службы', description: 'Кто принял решение, когда в записи вместо ФИО стоит служебный логин; строчными' },
  helpLabel: { id: 'quarantine.case.helpLabel', defaultMessage: 'Пояснение к делу', description: 'Подпись кнопки «?» с пояснением внизу дела контура (скринридер)' },
  decisionBy: { id: 'quarantine.case.decision.by', defaultMessage: 'ответственный: {name}', description: 'Кто принял решение или направил в агроинспекцию; {name} — ответственный карантинной службы' },
  newDeadline: { id: 'quarantine.case.decision.newDeadline', defaultMessage: 'новый срок {date}', description: 'Новый срок предписания после решения «без красного»; {date} — дата' },
  referral: { id: 'quarantine.case.referral', defaultMessage: 'Направлено в агроинспекцию', description: 'Заголовок события дела контура: красная единица земли направлена в агроинспекцию' },
  requirementTitle: { id: 'quarantine.case.requirement.title', defaultMessage: 'Предписание хозяйству', description: 'Заголовок события дела контура: выдано предписание хозяйству' },
  requirementText: { id: 'quarantine.case.requirement.text', defaultMessage: '{method} · срок {deadline}', description: 'Текст события «Предписание хозяйству»; {method} — метод устранения (или «метод не назван»), {deadline} — дата срока или «не назван»' },
  methodUnnamed: { id: 'quarantine.case.requirement.methodUnnamed', defaultMessage: 'метод не назван', description: 'Вместо метода устранения в предписании, когда он не назван; строчными' },
  deadlineUnnamed: { id: 'quarantine.case.requirement.deadlineUnnamed', defaultMessage: 'не назван', description: 'Вместо срока предписания, когда срок не назван; строчными, после «срок»' },
  workReport: { id: 'quarantine.case.work.report', defaultMessage: 'Отчёт хозяйства об обработке', description: 'Заголовок события дела контура: хозяйство отчиталось об обработке (это не устранение)' },
  workPlain: { id: 'quarantine.case.work.plain', defaultMessage: 'Работа хозяйства', description: 'Заголовок события дела контура: работа хозяйства по предписанию (отчёта ещё нет)' },
  followup: { id: 'quarantine.case.followup', defaultMessage: 'Повторный замер', description: 'Заголовок события дела контура: повторный замер инспектора' },
  eliminated: { id: 'quarantine.case.eliminated', defaultMessage: 'Устранено', description: 'Заголовок события дела контура: очаг устранён по замеру инспектора' },
  statusTitle: { id: 'quarantine.case.status.title', defaultMessage: 'Статус: {status}', description: 'Заголовок события дела контура: смена статуса; {status} — зелёный/жёлтый/красный' },
  statusDecision: { id: 'quarantine.case.status.decision', defaultMessage: 'решение: {name}', description: 'Кто принял решение о смене статуса; {name} — ответственный' },
  nowTitle: { id: 'quarantine.case.now.title', defaultMessage: 'Сейчас: {state}', description: 'Заголовок последней строки дела контура: состояние предписания на дату экрана; {state} — «просрочено», «устранено в срок»…' },
  nowOverdue: {
    id: 'quarantine.case.now.overdue',
    defaultMessage: '{days, plural, one {# день} few {# дня} many {# дней} other {# дня}} после срока, устранение не зафиксировано',
    description: 'Текст последней строки дела контура; # — дни после срока (считает сервер)',
  },
  noDate: { id: 'quarantine.case.noDate', defaultMessage: 'без даты', description: 'Вместо даты события в деле контура, когда дата не названа; строчными' },
  contourCard: { id: 'quarantine.case.contourCard', defaultMessage: 'Карточка контура →', description: 'Ссылка внизу дела контура: открыть карточку контура' },
  footNote: {
    id: 'quarantine.case.footNote',
    defaultMessage:
      'В базе пока нет: документов и комментариев к находке. Класс вида — по официальному перечню (A1/A2 — №3597, A3 — КМ №311), если вид в нём найден. Отчёт хозяйства об обработке — не устранение: устранение подтверждает замер инспектора или акт службы, и по нему цвет меняется сам, а работы по предписанию закрываются; красный ставит решение службы.',
    description: 'Примечание внизу дела контура: чего в базе нет и чем отличается отчёт хозяйства от устранения',
  },
});

const DECISION: Record<string, Message> = { set_red: m.decisionRed, lift_red: m.decisionLift, keep: m.decisionKeep };
const DIRECTION: Record<string, Message> = { pests: m.dirPests, diseases: m.dirDiseases, weeds: m.dirWeeds };
const SCALE: Record<string, Message> = { spot: m.scaleSpot, focus: m.scaleSpot, partial: m.scalePartial, whole: m.scaleWhole };
const OUTCOME: Record<string, Message> = {
  eliminated: m.outcomeEliminated,
  reduced: m.outcomeReduced,
  unchanged: m.outcomeUnchanged,
  increased: m.outcomeIncreased,
};
const STATUS: Record<string, Message> = { green: m.statusGreen, yellow: m.statusYellow, red: m.statusRed };
const STATE: Record<string, Message> = {
  open: m.stateOpen,
  overdue: m.stateOverdue,
  extended: m.stateExtended,
  eliminated: m.stateEliminated,
  eliminated_late: m.stateEliminatedLate,
};

/** Слово из таблицы сообщений по коду; незнакомый код показывается как есть. */
const word = (dict: Record<string, Message>, code: string): string => (dict[code] ? tr(dict[code]!) : code);

/** Класс вида подробно; «не указан» в деле называется своей фразой. */
const objectClass = (code: string): string =>
  code === 'unspecified' ? tr(m.classUnspecified) : (CLASS_LONG as Record<string, string>)[code] ?? code;

function haText(microha: number | null | undefined): string | null {
  if (microha == null) return null;
  return tr(m.ha, { value: formatNum(microha / 1_000_000, { maximumFractionDigits: 2 }) });
}

// i18n-ignore: правит русский текст причины, пришедший с сервера (номера записей убираются)
function cause(text: string): string {
  return text.replace(/повторное измерение #\d+/, 'повторный замер').replace(/находка #\d+/, 'находка');
}

/** Служебный логин («qofficer»), а не ФИО: показываем роль. */
const isLogin = (name: string): boolean => /^[a-z][a-z0-9_.-]*$/i.test(name.trim());

/** Пометки генератора демо в тексте решения («демо: …») пользователю не нужны. */
// i18n-ignore: фильтр служебной пометки русского текста с сервера
function cleanReason(text: string | null | undefined): string {
  return (text ?? '')
    .replace(/\s*[(;]\s*(?:правило помечает[^;)]*;\s*)?демо:[^)]*\)?/gi, '')
    .replace(/\s*демо:[^.;)]*/gi, '')
    .trim();
}

/** Выбор цикла работы по предписанию одним нажатием; отказ сервера — словами. */
function WorkCycleChoice({ work, onDone }: { work: { obligation_id: number; cycle_options: CycleOption[] }; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <CycleChoiceButtons
        options={work.cycle_options}
        onChoose={async (choice) => {
          if (!('cycle_id' in choice)) return false;
          setError(null);
          try {
            await api.chooseQuarantineWorkCycle(work.obligation_id, choice.cycle_id);
            onDone();
            return true;
          } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause));
            return false;
          }
        }}
      />
      {error ? <span className="qcase__text" role="alert">{error}</span> : null}
    </>
  );
}

export function QuarantineCase({ contourId, params, asOf, contourHref }: { contourId: string; params: QueryParams; /** Срез, названный контекстом; пусто — «сегодня» сервера из ответа (`horizon`). */ asOf: string | null; contourHref: string }) {
  const t = useT();
  const id = Number(contourId);
  const detail = useRequest(() => request<ContourDetail>(`/contours/${id}`, params), [id, JSON.stringify(params)]);
  const history = useRequest(() => request<ContourHistory>(`/contours/${id}/history`, params), [id, JSON.stringify(params)]);

  if (failed(detail) || failed(history)) {
    return <LoadError compact what={t(m.loadWhat)} error={(detail.error ?? history.error)!} onRetry={() => { detail.reload(); history.reload(); }} />;
  }
  if (!detail.data || !history.data) {
    return <div className="qcase" aria-busy="true"><Skeleton width="60%" /><Skeleton width="80%" /><Skeleton width="50%" /></div>;
  }

  const q = detail.data.quarantine;
  const events: Event[] = [];
  // Плановые обследования ДО первой находки — одной строкой: они говорят
  // «смотрели и не находили», но пять одинаковых строк заслоняли саму находку.
  const firstFinding = (q?.findings ?? []).map((f) => f.occurred_on).filter(Boolean).sort()[0] ?? null;
  const before = (q?.inspections ?? []).filter((i) => firstFinding && i.occurred_on && i.occurred_on < firstFinding);
  if (before.length) {
    const dates = before.map((i) => i.occurred_on!).sort();
    events.push({ date: dates[0]!, kind: 'inspection', stage: 'finding', title: t(m.beforeTitle, { count: num(before.length) }), text: before.length > 1 ? t(m.beforeRange, { from: dateShort(dates[0]!), to: dateShort(dates[dates.length - 1]!) }) : undefined, tone: 'muted' });
  }
  for (const i of (q?.inspections ?? []).filter((x) => !before.includes(x))) {
    events.push({ date: i.occurred_on, kind: 'inspection', stage: 'finding', title: t(m.inspection), text: t(m.inspector, { name: i.inspector || t(m.inspectorUnknown), hasPart: i.part_label ? 'yes' : 'no', part: i.part_label ?? '' }), tone: 'muted' });
  }
  const officer = (name: string | null | undefined): string | null =>
    name ? (isLogin(name) ? t(m.officerRole) : t(m.decisionBy, { name })) : null;
  const partLabel = new Map((detail.data.parts ?? []).map((p) => [p.id, p.label]));
  for (const f of q?.findings ?? []) {
    const signal = haText(f.signal_microha);
    events.push({
      date: f.occurred_on,
      kind: 'finding',
      stage: 'finding',
      title: t(m.findingTitle, { name: f.problem_name ?? t(m.speciesUnnamed) }),
      text: [
        word(DIRECTION, f.direction),
        f.object_class ? objectClass(f.object_class) : null,
        f.part_id != null ? t(m.findingPart, { part: partLabel.get(f.part_id) ?? f.part_id }) : t(m.findingWhole),
        signal ? t(m.findingSignal, { area: signal, unspecified: f.area_basis === 'whole_unspecified' ? 'yes' : 'no' }) : null,
        f.scale ? word(SCALE, f.scale) : null,
        f.case_state ? CASE_STATE_WORD[f.case_state] ?? f.case_state : null,
        f.outside_cycle_reason ? OUTSIDE_CYCLE_WORD[f.outside_cycle_reason] ?? f.outside_cycle_reason : null,
        f.note?.split('. Справочной строки')[0] ?? null,
      ].filter(Boolean).join(' · '),
      tone: f.case_state === 'awaiting_decision' || f.case_state === 'no_addressee' ? 'yellow' : undefined,
    });
  }
  for (const d of q?.decisions ?? []) {
    events.push({
      date: d.decided_on,
      kind: 'decision',
      stage: 'resolved',
      title: word(DECISION, d.kind),
      text: [officer(d.decided_by), cleanReason(d.reason) || null, d.new_deadline ? t(m.newDeadline, { date: dateShort(d.new_deadline) }) : null].filter(Boolean).join(' · '),
      tone: d.kind === 'set_red' ? 'red' : d.kind === 'lift_red' ? 'green' : 'muted',
    });
  }
  for (const r of q?.referrals ?? []) {
    events.push({
      date: r.referred_on,
      kind: 'decision',
      stage: 'resolved',
      title: t(m.referral),
      text: [officer(r.referred_by), cleanReason(r.basis) || null].filter(Boolean).join(' · '),
      tone: 'red',
    });
  }
  for (const r of history.data.requirements ?? []) {
    const req = r.requirement;
    if (!req) continue;
    events.push({
      date: req.issued_on,
      kind: 'requirement',
      stage: 'requirement',
      title: t(m.requirementTitle),
      text: t(m.requirementText, { method: req.method || t(m.methodUnnamed), deadline: req.deadline ? dateShort(req.deadline) : t(m.deadlineUnnamed) }),
    });
    const reportedOn = (req.works ?? []).map((w) => w.reported_on).filter((d): d is string => Boolean(d)).sort().at(-1);
    const work = findingWorkText(req.works, Boolean(req.eliminated_on));
    // Работа в приёмнике цикла: ссылка на страницу работы цикла (К9, S4).
    const linked = (req.works ?? []).filter((w) => w.cycle_key && w.campaign_id);
    if (work) {
      events.push({
        date: reportedOn ?? req.issued_on,
        kind: 'requirement',
        stage: 'report',
        title: reportedOn ? t(m.workReport) : t(m.workPlain),
        text: (
          <>
            {work}
            {linked.length > 0 ? ' · ' : ''}
            {linked.slice(0, 3).map((w, i) => (
              <span key={w.obligation_id}>
                {i > 0 ? ', ' : ''}
                <WorkCycleLink work={w} />
              </span>
            ))}
          </>
        ),
        tone: reportedOn ? 'muted' : 'yellow',
      });
    }
    // Два цикла на поле (№1038): работа ждёт выбора цикла «вне агроцикла».
    for (const w of (req.works ?? []).filter((x) => (x.cycle_options ?? []).length > 1)) {
      events.push({
        date: req.issued_on,
        kind: 'requirement',
        stage: 'report',
        title: tr(m.workAwaitsCycle),
        text: <WorkCycleChoice work={w} onDone={() => history.reload()} />,
        tone: 'yellow',
      });
    }
    for (const f of req.followups ?? []) {
      events.push({ date: f.occurred_on, kind: 'followup', stage: 'followup', title: t(m.followup), text: word(OUTCOME, f.outcome), tone: f.outcome === 'eliminated' ? 'green' : f.outcome === 'reduced' ? 'muted' : 'yellow' });
    }
    if (req.eliminated_on) events.push({ date: req.eliminated_on, kind: 'followup', stage: 'followup', title: t(m.eliminated), tone: 'green' });
  }
  for (const s of q?.intervals ?? []) {
    if (!s.from_date) continue;
    const where = s.part_id != null ? t(m.findingPart, { part: partLabel.get(s.part_id) ?? s.part_id }) : null;
    events.push({ date: s.from_date, kind: 'status', title: t(m.statusTitle, { status: word(STATUS, s.status) }), text: [where, cleanReason(cause(s.cause)), s.decided_by ? (isLogin(s.decided_by) ? t(m.officerRole) : t(m.statusDecision, { name: s.decided_by })) : null].filter(Boolean).join(' · '), tone: s.status === 'red' ? 'red' : s.status === 'yellow' ? 'yellow' : 'green' });
  }
  const order: Record<Event['kind'], number> = { inspection: 0, finding: 1, requirement: 2, followup: 3, decision: 4, status: 5, now: 6 };
  events.sort((a, b) => (a.date ?? '').localeCompare(b.date ?? '') || order[a.kind] - order[b.kind]);

  const open = (history.data.requirements ?? []).map((r) => r.requirement).filter(Boolean);
  // «Сейчас» — по ПОСЛЕДНЕМУ выданному предписанию (сервер отдаёт новые
  // первыми): у продлённой цепочки старое — «срок продлён», а дело живёт в
  // продолжении (№1650).
  const last = [...open].sort((a, b) => a.issued_on.localeCompare(b.issued_on) || a.id - b.id).at(-1);
  if (last) {
    // Дни после срока — считает сервер (S4), клиент дат не вычитает.
    const overdueDays = last.overdue_days ?? null;
    events.push({
      date: asOf ?? history.data.horizon,
      kind: 'now',
      title: t(m.nowTitle, { state: word(STATE, last.state) }),
      text: overdueDays ? t(m.nowOverdue, { days: overdueDays }) : undefined,
      tone: last.state === 'overdue' ? 'red' : last.state?.startsWith('eliminated') ? 'green' : 'muted',
    });
  }

  return (
    <div className="qcase">
      <ol className="qcase__list">
        {events.map((e, i) => (
          <li key={i} className={`qcase__ev qcase__ev--${e.kind}${e.tone ? ` is-${e.tone}` : ''}`}>
            <time className="qcase__date">{e.date ? dateShort(e.date) : t(m.noDate)}</time>
            {e.stage ? (
              <span className="qcase__dot qcase__dot--icon" aria-hidden="true"><Icon icon={STAGE_ICON[e.stage] as LucideIcon} size={16} /></span>
            ) : (
              <span className="qcase__dot" aria-hidden="true" />
            )}
            <span className="qcase__body">
              <strong>{e.title}</strong>
              {e.text ? <span className="qcase__text">{e.text}</span> : null}
            </span>
          </li>
        ))}
      </ol>
      <QuarantineEntry
        contourId={id}
        parts={(detail.data.parts ?? []).map((p) => ({ id: p.id, label: p.label }))}
        findings={(q?.findings ?? []).map((f) => ({ id: f.id, problem_name: f.problem_name, case_state: f.case_state, awaiting_reason: f.awaiting_reason, object_class: f.object_class }))}
        requirements={(history.data.requirements ?? []).map((r) => ({ id: r.requirement.id, finding_id: r.finding.id }))}
        onDone={() => { detail.reload(); history.reload(); }}
      />
      {/* `div`, а не `p`: внутри — `details` с абзацем, а `p` в `p` (и `details` в `p`) — неверная вложенность. */}
      <div className="qcase__foot">
        <Link to={contourHref}>{t(m.contourCard)}</Link>
        <details className="qcase__help">
          <summary aria-label={t(m.helpLabel)} title={t(m.helpLabel)}>?</summary>
          <p>{t(m.footNote)}</p>
        </details>
      </div>
    </div>
  );
}
